import React, { useRef } from 'react';
import styled from 'styled-components';

import {
  convertMMSSToSeconds,
  convertTimeToMMSS,
} from '../../utility/timeFormat';
import Checkbox from '../common/Checkbox';
import {
  DialogCard,
  DialogFooter,
  DialogHeader,
  DialogSubtitle,
  DialogTitle,
  PrimaryButton,
  SecondaryButton,
} from '../common/dialogStyles';
import Input from '../common/Input';
import Modal from '../common/Modal';
import Textarea from '../common/Textarea';

/** Keep the transcript's global key handlers out of the dialog's fields. */
const stopKeys = (
  e: React.KeyboardEvent<HTMLInputElement | HTMLTextAreaElement>,
) => {
  e.stopPropagation();
  e.nativeEvent.stopImmediatePropagation();
  e.nativeEvent.stopPropagation();
};

export const WordReplacementModalUI: React.FC<{
  currentText: string;
  type: 'input' | 'textarea';
  submitText: (text: string) => void;
  discard: () => void;
  currentRange?: { start: number; end: number };
  changeRange?: (range: { start: number; end: number }) => void;
  setShouldReplaceAll?: (value: boolean) => void;
  shouldReplaceAll?: boolean;
  setShouldMatchCase?: (value: boolean) => void;
  shouldMatchCase?: boolean;
  setShouldAddToDictionary?: (value: boolean) => void;
  shouldAddToDictionary?: boolean;
}> = ({
  currentText,
  type,
  currentRange,
  changeRange,
  submitText,
  discard,
  setShouldReplaceAll,
  shouldReplaceAll = false,
  setShouldMatchCase,
  shouldMatchCase = false,
  setShouldAddToDictionary,
  shouldAddToDictionary = false,
}) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const textAreaRef = useRef<HTMLTextAreaElement>(null);
  const rangeStartRef = useRef<HTMLInputElement>(null);
  const rangeEndRef = useRef<HTMLInputElement>(null);

  const term = currentText.trim();

  return (
    <Modal isOpen={true} closeModal={discard}>
      <DialogCard>
        <DialogHeader>
          <DialogTitle>Replace Term</DialogTitle>
          {term && <DialogSubtitle title={term}>{`“${term}”`}</DialogSubtitle>}
        </DialogHeader>
        <FieldSet>
          {currentRange && (
            <Ranges>
              <Input
                type="time"
                step={0.001}
                ref={rangeStartRef}
                defaultValue={
                  '00:' + convertTimeToMMSS(currentRange.start, true)
                }
                onKeyDownCapture={stopKeys}
                onKeyDown={stopKeys}
              />
              <RangeArrow>{'-->'}</RangeArrow>
              <Input
                type="time"
                step={0.001}
                ref={rangeEndRef}
                defaultValue={'00:' + convertTimeToMMSS(currentRange.end, true)}
                onKeyDownCapture={stopKeys}
                onKeyDown={stopKeys}
              />
            </Ranges>
          )}
          {type === 'textarea' ? (
            <Textarea
              label="Edit Transcription"
              fullWidth
              defaultValue={currentText}
              ref={textAreaRef}
              onKeyDownCapture={stopKeys}
              onKeyDown={stopKeys}
            />
          ) : (
            <Input
              label="Edit Transcription"
              fullWidth
              type="text"
              defaultValue={currentText}
              ref={inputRef}
              onKeyDownCapture={stopKeys}
              onKeyDown={stopKeys}
              data-testid="word-replacement-input"
            />
          )}
          <CheckboxSection>
            {setShouldMatchCase && (
              <Checkbox
                label="Match Case"
                color={shouldMatchCase ? 'var(--primary)' : 'var(--background)'}
                borderColor={
                  shouldMatchCase ? 'var(--primary)' : 'var(--border)'
                }
                labelColor="var(--text-secondary)"
                onClick={() => {
                  setShouldMatchCase(!shouldMatchCase);
                }}
                checked={shouldMatchCase}
              />
            )}
            {setShouldReplaceAll && (
              <Checkbox
                label="Replace All Instances"
                color={
                  shouldReplaceAll ? 'var(--primary)' : 'var(--background)'
                }
                borderColor={
                  shouldReplaceAll ? 'var(--primary)' : 'var(--border)'
                }
                labelColor="var(--text-secondary)"
                onClick={() => {
                  setShouldReplaceAll(!shouldReplaceAll);
                }}
                checked={shouldReplaceAll}
              />
            )}
            {setShouldAddToDictionary && (
              <Checkbox
                label="Add to Company Dictionary"
                color={
                  shouldAddToDictionary ? 'var(--primary)' : 'var(--background)'
                }
                borderColor={
                  shouldAddToDictionary ? 'var(--primary)' : 'var(--border)'
                }
                labelColor="var(--text-secondary)"
                onClick={() => {
                  setShouldAddToDictionary(!shouldAddToDictionary);
                }}
                checked={shouldAddToDictionary}
              />
            )}
          </CheckboxSection>
        </FieldSet>
        <DialogFooter>
          <SecondaryButton onClick={discard}>Cancel</SecondaryButton>
          <PrimaryButton
            onClick={() => {
              submitText((inputRef.current || textAreaRef.current)?.value!);
              if (
                changeRange &&
                rangeStartRef.current?.value &&
                rangeEndRef.current?.value
              ) {
                changeRange({
                  start: convertMMSSToSeconds(
                    rangeStartRef.current?.value.substring(3),
                  ),
                  end: convertMMSSToSeconds(
                    rangeEndRef.current?.value.substring(3),
                  ),
                });
              }
            }}
          >
            {shouldReplaceAll ? 'Replace All' : 'Replace'}
          </PrimaryButton>
        </DialogFooter>
      </DialogCard>
    </Modal>
  );
};

const FieldSet = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--spacing-md);
`;

const Ranges = styled.div`
  display: flex;
  align-items: center;
  gap: var(--spacing-sm);
  justify-content: space-between;
`;

const RangeArrow = styled.span`
  font-size: var(--text-xs);
  color: var(--text-muted);
`;

const CheckboxSection = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: var(--spacing-sm);
`;
